import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

type UtmData = {
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;
};

type InboundCampaignRuleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    data: { name: string; messagePattern: string; projectId?: string } & UtmData
  ) => Promise<void>;
  initialData?: ({ name: string; messagePattern: string; projectId?: string } & UtmData) | null;
  merchantType?: "rentals" | "development" | null;
  projects?: { id: string; name: string }[];
};

const InboundCampaignRuleModal = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  merchantType,
  projects,
}: InboundCampaignRuleModalProps) => {
  const [name, setName] = useState("");
  const [messagePattern, setMessagePattern] = useState("");
  const [projectId, setProjectId] = useState("");
  const [utmSource, setUtmSource] = useState("");
  const [utmMedium, setUtmMedium] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmTerm, setUtmTerm] = useState("");
  const [utmContent, setUtmContent] = useState("");
  const [showUtm, setShowUtm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setName(initialData?.name ?? "");
      setMessagePattern(initialData?.messagePattern ?? "");
      setProjectId(initialData?.projectId ?? "");
      setUtmSource(initialData?.utmSource ?? "");
      setUtmMedium(initialData?.utmMedium ?? "");
      setUtmCampaign(initialData?.utmCampaign ?? "");
      setUtmTerm(initialData?.utmTerm ?? "");
      setUtmContent(initialData?.utmContent ?? "");
      setShowUtm(
        !!(
          initialData?.utmSource ||
          initialData?.utmMedium ||
          initialData?.utmCampaign ||
          initialData?.utmTerm ||
          initialData?.utmContent
        )
      );
    }
  }, [isOpen, initialData]);

  const handleClose = () => {
    setName("");
    setMessagePattern("");
    setProjectId("");
    setUtmSource("");
    setUtmMedium("");
    setUtmCampaign("");
    setUtmTerm("");
    setUtmContent("");
    setShowUtm(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!name.trim() || !messagePattern.trim()) return;
    setIsSubmitting(true);
    try {
      await onSubmit({
        name: name.trim(),
        messagePattern: messagePattern.trim(),
        ...(projectId && { projectId }),
        ...(utmSource.trim() && { utmSource: utmSource.trim() }),
        ...(utmMedium.trim() && { utmMedium: utmMedium.trim() }),
        ...(utmCampaign.trim() && { utmCampaign: utmCampaign.trim() }),
        ...(utmTerm.trim() && { utmTerm: utmTerm.trim() }),
        ...(utmContent.trim() && { utmContent: utmContent.trim() }),
      });
      handleClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  const isEdit = !!initialData;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="h-full max-h-full md:h-auto md:max-h-[85vh] md:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Editar Pauta" : "Asociar Pauta"}</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-4 py-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="campaign-name">Nombre</Label>
            <Input
              id="campaign-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: Pauta Pereira"
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="campaign-pattern">Patron de mensaje</Label>
            <Input
              id="campaign-pattern"
              value={messagePattern}
              onChange={(e) => setMessagePattern(e.target.value)}
              placeholder="Ej: arriendos en pereira"
            />
            <p className="text-xs text-gray-500">
              Texto que debe contener el primer mensaje del lead. Usa {"{variable}"} para partes que
              cambian. Ejemplo: "interesado en el inmueble {"{codigo}"}" coincide con "interesado en
              el inmueble 12345".
            </p>
          </div>
          {merchantType === "development" && (
            <div className="flex flex-col gap-2">
              <Label htmlFor="campaign-project">Proyecto (opcional)</Label>
              <select
                id="campaign-project"
                value={projectId}
                onChange={(e) => setProjectId(e.target.value)}
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs transition-colors focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-none"
              >
                <option value="">Sin proyecto</option>
                {projects?.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          )}
          {/* UTM Parameters (collapsible) */}
          <div className="flex flex-col gap-2">
            <button
              type="button"
              onClick={() => setShowUtm(!showUtm)}
              className="flex items-center gap-1 text-sm font-medium text-gray-600 hover:text-gray-800"
            >
              <ChevronRight className={cn("size-4 transition-transform", showUtm && "rotate-90")} />
              Parametros UTM (opcional)
            </button>
            {showUtm && (
              <div className="flex flex-col gap-3 pl-5">
                <div className="flex flex-col gap-1">
                  <Label htmlFor="utm-source">Fuente</Label>
                  <Input
                    id="utm-source"
                    value={utmSource}
                    onChange={(e) => setUtmSource(e.target.value)}
                    placeholder="Ej: facebook"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="utm-medium">Medio</Label>
                  <Input
                    id="utm-medium"
                    value={utmMedium}
                    onChange={(e) => setUtmMedium(e.target.value)}
                    placeholder="Ej: cpc"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="utm-campaign">Campaña</Label>
                  <Input
                    id="utm-campaign"
                    value={utmCampaign}
                    onChange={(e) => setUtmCampaign(e.target.value)}
                    placeholder="Ej: arriendos-pereira-2026"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="utm-term">Termino</Label>
                  <Input
                    id="utm-term"
                    value={utmTerm}
                    onChange={(e) => setUtmTerm(e.target.value)}
                    placeholder="Ej: arriendos pereira"
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <Label htmlFor="utm-content">Contenido</Label>
                  <Input
                    id="utm-content"
                    value={utmContent}
                    onChange={(e) => setUtmContent(e.target.value)}
                    placeholder="Ej: banner-hero"
                  />
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={handleClose} disabled={isSubmitting}>
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting || !name.trim() || !messagePattern.trim()}
          >
            {isSubmitting ? "Guardando..." : isEdit ? "Guardar cambios" : "Asociar pauta"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default InboundCampaignRuleModal;
